import * as React from 'react';
import {FunctionComponent, useLayoutEffect} from 'react';
import {Enter} from './enter';
import {LoginState} from '../../shared/models/login/loginStep';
import {Login} from './login';
import {RegistrationOrRecovery} from './registratonOrRecovery';
import {useDispatch, useSelector} from 'react-redux';
import {LoginStepSelector} from '../../redux/selectors/runtime/loginStep';
import {LoginLayout} from './components/layout';
import {
  esiaCodeSelector,
  esiaUserHasNoCardsSelector,
  esiaUserNotAcceptSelector,
  loginResponseSelector,
} from '../../redux/selectors/login';
import {LoginType} from '../../shared/models/api';
import {useDeviceSize} from '../../shared/theme/helpers';
import {Esia} from './esia';
import {DecoratedComponentClass} from 'redux-form';
import {EsiaRegCard} from './esiaRegCard';
import {useStyles} from '../../shared/hooks/styles';
import {Platform, ViewStyle} from 'react-native';
import {esiaLogin} from '../../redux/actions/login';
import {WebView} from 'react-native-webview';
import {PhoneCheck} from './phoneCheck';
import {useAccountLinker} from '../../shared/hooks/useAccountLinkerHook/useAccountLinker';
import {useIsSmallScreenDevice} from '../../shared/hooks/useIsSmallScreenDevice';

export default ({esiaLogoutLink}: {esiaLogoutLink: string | null}) => {
  const dispatch = useDispatch();
  const deviceSize = useDeviceSize();
  const step = useSelector(LoginStepSelector());

  const loginResponse = useSelector(loginResponseSelector());
  const esiaCodeState = useSelector(esiaCodeSelector);
  const esiaUserNotAccept = useSelector(esiaUserNotAcceptSelector);
  const esiaUserHasNoCards = useSelector(esiaUserHasNoCardsSelector);

  const [title, Content] = React.useMemo(() => {
    let titleStr = 'Восстановление пароля';
    switch (step) {
      case LoginState.enter:
      case LoginState.login:
      case LoginState.phoneNumberCheck:
        titleStr = 'Вход в личный кабинет' + (process.env.NODE_ENV === 'development' ? ' [Тестовый]' : '');
        break;
      case LoginState.registrationCode:
      case LoginState.registrationPasswords:
        titleStr = 'Регистрация';
        break;
      case LoginState.agreement:
        titleStr = 'Согласие на обработку персональных данных';
        break;
      case LoginState.esiaLogin:
        titleStr = 'Ваша учетная запись\nв ЕСИА не подтверждена';
        break;
      case LoginState.esiaRegCardUnconfirmed:
        titleStr = 'У Вас нет Единой\nсоциальной карты';
        break;
    }

    let content: FunctionComponent | DecoratedComponentClass<any, any> = RegistrationOrRecovery;
    switch (step) {
      case LoginState.enter:
        content = Enter;
        break;
      case LoginState.login:
        content = Login;
        break;
      case LoginState.esiaLogin:
        content = Esia;
        break;
      case LoginState.esiaRegCardUnconfirmed:
        content = EsiaRegCard;
        break;
      case LoginState.phoneNumberCheck:
        content = PhoneCheck;
    }

    return [titleStr, content];
  }, [step]);
  const showBackButton = React.useMemo(() => {
    if (step === LoginState.esiaRegCardUnconfirmed && esiaUserHasNoCards) return false;

    return (
      step !== LoginState.enter ||
      (step === LoginState.enter && loginResponse && loginResponse.loginType !== LoginType.EskNumber)
    );
  }, [step, loginResponse, esiaUserHasNoCards]);
  const showHelpText = React.useMemo(() => {
    if (step === LoginState.enter) {
      return !(loginResponse && loginResponse.loginType !== LoginType.EskNumber);
    }
    return false;
  }, [step, deviceSize, loginResponse]);

  const isEsia = step === LoginState.esiaLogin;
  const isEsiaMobile = isEsia && Platform.OS !== 'web' && !esiaUserNotAccept;

  const isSmallScreen = useIsSmallScreenDevice();

  const styles = useStyles<Partial<Record<LoginState, ViewStyle>>>(
    () => ({
      [LoginState.esiaLogin]: isEsiaMobile
        ? {
            width: '100%',
            height: isSmallScreen ? '50%' : 'auto',
            ...(isSmallScreen ? {} : {flex: 1}),
            paddingHorizontal: 0,
            paddingTop: 0,
            paddingBottom: 0,
          }
        : undefined,
      [LoginState.esiaRegCardUnconfirmed]: {
        minHeight: 390,
      },
    }),
    [isEsiaMobile],
  );

  useLayoutEffect(() => {
    const {code, state} = esiaCodeState;
    if (!code || !state) return;

    dispatch(esiaLogin());
  }, [esiaCodeState]);
  const {resetLinkingAccountState} = useAccountLinker();
  React.useEffect(() => {
    resetLinkingAccountState();
  }, []);

  return (
    <LoginLayout
      useBackButton={!!showBackButton}
      title={title}
      showHelpText={showHelpText}
      containerStyle={styles[step]}
      showLogo={!isEsiaMobile}
      showTitle={!isEsiaMobile}>
      <Content />
      {esiaLogoutLink && Platform.OS !== 'web' ? (
        <WebView
          style={{width: 0, height: 0}}
          source={{uri: esiaLogoutLink}}
          androidHardwareAccelerationDisabled
          thirdPartyCookiesEnabled
          cacheEnabled={false}
        />
      ) : null}
    </LoginLayout>
  );
};
